import { useEffect, useMemo, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { DashboardWidgetWrapper } from '@/pages/organization/components/DashboardWidgetWrapper';
import {
  getDefaultTimeRange,
  TimeRangeSelector,
  type TimeFilter,
} from '@/components/TimeRangeSelector';
import type { DisplayTimeZone } from '@/components/TimeRangeSelector/types';
import {
  formatDateInDisplayTimeZone,
  getBrowserTimeZone,
} from '@/components/TimeRangeSelector/utils';
import { useOrganizationDashboardAlerts } from '@/hooks/api/useOrganizationDashboardAlerts';
import { useOrganization } from '@/hooks/api/useOrganizations';
import { useParams } from 'react-router-dom';

type AlertsPoint = {
  date: string;
  label: string;
  count: number;
};

function formatTooltipTitleWithTimezone(
  p: AlertsPoint,
  timezone: DisplayTimeZone
): string {
  const base = formatDateInDisplayTimeZone(p.date, timezone, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  // We can tweak per-bucket formatting later if needed; for now,
  // show a full date-time in the selected timezone.
  return base;
}

function AlertsTooltip({
  active,
  payload,
  timezone,
}: {
  active?: boolean;
  payload?: { payload: AlertsPoint }[];
  bucketType?: 'minutes' | 'hours' | 'days' | 'weeks' | 'months' | 'years';
  timezone: DisplayTimeZone;
}) {
  if (!active || !payload?.length) return null;
  const p = payload[0]?.payload as AlertsPoint | undefined;
  if (!p) return null;

  return (
    <div className="rounded-lg border border-border bg-background px-3 py-2 shadow-lg">
      <div className="text-xs text-muted-foreground mb-1">
        {formatTooltipTitleWithTimezone(p, timezone)}
      </div>
      <div className="text-sm font-semibold text-foreground">
        {p.count} alerts
      </div>
    </div>
  );
}

/**
 * AlertsWidget (layout-first)
 * - Matches the reference layout: title + time-range selector (underline) on the left,
 *   total count on the right.
 * - Recharts bar chart with rounded bars, subtle emphasis on active bar, tooltip.
 */
export const AlertsWidget: React.FC = () => {
  const { organizationId } = useParams<{ organizationId: string }>();
  const { data: organization } = useOrganization(organizationId);
  const [range, setRange] = useState<TimeFilter>(() => ({
    range: getDefaultTimeRange('browser'),
    timezone: 'browser',
  }));
  const fromParam = range.range.from.toISOString();
  const toParam = range.range.to.toISOString();
  const resolvedTz =
    range.timezone === 'browser' ? getBrowserTimeZone() : range.timezone;
  const isEnabled = Boolean(!!organizationId && fromParam && toParam);
  const { isLoading, data, isError, refetch } = useOrganizationDashboardAlerts(
    organizationId,
    fromParam,
    toParam,
    isEnabled,
    resolvedTz
  );

  const total = data?.meta.total ?? 0;
  const bucketType = data?.meta.bucketType;
  const chartData: AlertsPoint[] = useMemo(() => data?.history ?? [], [data]);

  const [narrowScreen, setNarrowScreen] = useState(() => {
    if (
      typeof window === 'undefined' ||
      typeof window.matchMedia !== 'function'
    ) {
      return false;
    }
    return !window.matchMedia('(min-width: 768px)').matches;
  });
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(min-width: 768px)');
    const update = () => setNarrowScreen(!mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  const formatLine1 = (isoDate: string) => {
    if (!isoDate) return '';
    if (bucketType === 'minutes' || bucketType === 'hours') {
      return formatDateInDisplayTimeZone(isoDate, range.timezone, {
        hour: '2-digit',
        minute: '2-digit',
      });
    }
    return formatDateInDisplayTimeZone(isoDate, range.timezone, {
      month: 'short',
      day: 'numeric',
    });
  };

  const formatLine2 = (isoDate: string) => {
    if (!isoDate || bucketType === 'minutes' || bucketType === 'hours')
      return '';
    return formatDateInDisplayTimeZone(isoDate, range.timezone, {
      year: 'numeric',
    });
  };

  // Dynamic label styling based on number of data points
  const dataCount = chartData.length;
  const labelFontSize = dataCount > 20 ? 8 : dataCount > 12 ? 9 : 10;
  const labelAngle = dataCount > 15 ? -45 : dataCount > 10 ? -30 : 0;

  const renderXAxisTick = (props: {
    x?: string | number;
    y?: string | number;
    payload?: { value?: string };
  }) => {
    const x = Number(props.x ?? 0);
    const y = Number(props.y ?? 0);
    const value = props.payload?.value ?? '';
    const line1 = formatLine1(value);
    const line2 = formatLine2(value);
    const twoLines = narrowScreen && line2;
    const fill = 'var(--color-muted-foreground)';

    // When rotated, use end anchor for better alignment
    const textAnchor = labelAngle !== 0 ? 'end' : 'middle';

    return (
      <text
        x={x}
        y={y}
        textAnchor={textAnchor}
        fill={fill}
        fontSize={labelFontSize}
        transform={
          labelAngle !== 0 ? `rotate(${labelAngle}, ${x}, ${y})` : undefined
        }
      >
        {twoLines ? (
          <>
            <tspan x={x} dy={0}>
              {line1}
            </tspan>
            <tspan x={x} dy={14}>
              {line2}
            </tspan>
          </>
        ) : (
          line1 + (line2 ? `, ${line2}` : '')
        )}
      </text>
    );
  };

  return (
    <DashboardWidgetWrapper
      className="xl:col-span-8"
      title="Alerts"
      isLoading={isLoading}
      isError={isError}
      refetch={refetch}
    >
      <div className="flex h-full w-full flex-col">
        {/* Header row inside the card content */}
        <div className="flex shrink-0 items-start justify-between">
          <div className="flex flex-col gap-1">
            {/* We already render "Alerts" as CardTitle; keep selector as the “sub-control” */}
            <div className="flex items-center gap-1">
              <TimeRangeSelector
                timeFilter={range}
                onChange={newRange => setRange(newRange)}
                minDate={organization?.createdAt}
              />
            </div>
          </div>

          <div className="text-right">
            <div className="text-md font-medium text-muted-foreground">
              Total:
            </div>
            <div className="text-[32px] font-bold">{total}</div>
          </div>
        </div>

        {/* Chart */}
        <div
          className="mt-3 w-full min-w-0 flex-1 min-h-[200px]"
          onMouseDown={e => e.preventDefault?.()}
        >
          <ResponsiveContainer width="100%" height="100%" minWidth={0}>
            <BarChart
              data={chartData}
              margin={{
                top: 6,
                right: 8,
                left: -32,
                bottom: labelAngle !== 0 ? 56 : narrowScreen ? 44 : 32,
              }}
              barCategoryGap={10}
              tabIndex={0}
            >
              <defs>
                <linearGradient id="alertsGradient" x1="0" y1="1" x2="0" y2="0">
                  <stop
                    offset="0%"
                    stopColor="var(--alerts-bar-top)"
                    stopOpacity={0.95}
                  />
                  <stop
                    offset="60%"
                    stopColor="var(--alerts-bar-mid)"
                    stopOpacity={0.7}
                  />
                  <stop
                    offset="100%"
                    stopColor="var(--alerts-bar-bottom)"
                    stopOpacity={0.4}
                  />
                </linearGradient>
              </defs>

              <XAxis
                dataKey="date"
                tickLine={false}
                tickMargin={8}
                axisLine={{ stroke: 'var(--color-border)', strokeWidth: 1 }}
                interval={0}
                tick={renderXAxisTick}
              />

              <YAxis
                tickLine={false}
                axisLine={false}
                domain={[0, 'dataMax + 2']}
                padding={{ top: 10, bottom: 10 }}
                tick={{ fill: 'var(--color-muted-foreground)', fontSize: 12 }}
              />

              <Tooltip
                content={
                  <AlertsTooltip
                    bucketType={bucketType}
                    timezone={range.timezone}
                  />
                }
                cursor={false}
                wrapperStyle={{ outline: 'none' }}
              />

              <Bar
                dataKey="count"
                fill="url(#alertsGradient)"
                radius={[8, 8, 4, 4]}
                maxBarSize={56}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </DashboardWidgetWrapper>
  );
};
